import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { MarketingTarotCard } from './MarketingTarotCard';
import { Button } from '@/components/ui/button';
import { 
  RotateCcw, 
  BookOpen, 
  Sparkles,
  ChevronDown,
  ChevronUp,
  Lock
} from 'lucide-react';

export const DrawnCard = ({ 
  card, 
  onReset,
  onPickAnother,
  sessionLocked = false,
  drawsLeft = 0,
  userName,
  className 
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);

  useEffect(() => {
    if (card) {
      setIsFlipped(false);
      setShowDetails(false);
      setAnimationComplete(false);
      
      // Auto flip after entrance animation
      const flipTimer = setTimeout(() => {
        setIsFlipped(true);
      }, 600);

      // Show details after flip
      const detailsTimer = setTimeout(() => {
        setAnimationComplete(true);
      }, 1400);

      return () => {
        clearTimeout(flipTimer);
        clearTimeout(detailsTimer);
      };
    }
  }, [card]);

  if (!card) return null;

  return (
    <div className={cn(
      "flex flex-col items-center gap-6",
      className
    )}>
      {/* Drawn Card Display */}
      <div className="relative flex-shrink-0">
        {/* Glow effect behind card */}
        <div className={cn(
          "absolute -inset-8 bg-[#e89b47]/15 blur-3xl rounded-full transition-opacity duration-1000",
          isFlipped ? "opacity-100" : "opacity-0"
        )}/>
        
        {/* The Card */}
        <div 
          className={cn(
            "relative transition-all duration-700 ease-out transform",
            !animationComplete && "animate-slide-up"
          )}
        >
          <MarketingTarotCard
            card={card}
            isFlipped={isFlipped}
            size="large"
            isHoverable={false}
          />
        </div>

        {/* Sparkle effects */}
        {isFlipped && (
          <div className="absolute -inset-4 pointer-events-none overflow-visible">
            <Sparkles className="absolute -top-2 -left-2 w-6 h-6 text-[#e89b47] animate-pulse" style={{ animationDelay: '0s' }}/>
            <Sparkles className="absolute top-8 -right-4 w-4 h-4 text-[#e89b47]/70 animate-pulse" style={{ animationDelay: '0.3s' }}/>
            <Sparkles className="absolute bottom-12 -left-6 w-5 h-5 text-[#e89b47]/80 animate-pulse" style={{ animationDelay: '0.6s' }}/>
            <Sparkles className="absolute -bottom-2 -right-2 w-4 h-4 text-[#e89b47]/60 animate-pulse" style={{ animationDelay: '0.9s' }}/>
          </div>
        )}
      </div>

      {/* Card Info Panel - Below the card */}
      {animationComplete && (
        <div className={cn(
          "w-full max-w-xs animate-fade-in",
          "bg-[#1e2a4a]/80 backdrop-blur-sm",
          "border-2 border-[#d4c4a8]/30 rounded-xl p-4"
        )}>
          <div className="text-center space-y-3">
            <h2 className="font-display text-2xl text-[#e8dcc8]">
              {card.name}
            </h2>
            
            {card.isReversed && (
              <span className="inline-block px-3 py-1 bg-[#e89b47]/20 border border-[#e89b47]/40 rounded-full text-[#e89b47] text-sm">
                Reversed Position
              </span>
            )}
            
            {/* Keywords */}
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {card.keywords?.map((keyword, idx) => (
                <span 
                  key={idx}
                  className="px-3 py-1 bg-[#d4c4a8]/10 border border-[#d4c4a8]/30 rounded-full text-[#d4c4a8]/80 text-sm font-body"
                >
                  {keyword}
                </span>
              ))}
            </div>

            {/* Expandable Meaning Section */}
            {card.meaning && (
              <div className="mt-4">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center justify-center gap-2 w-full py-2 text-[#d4c4a8]/70 hover:text-[#d4c4a8] transition-colors"
                >
                  <BookOpen className="w-4 h-4"/>
                  <span className="text-sm font-body">
                    {showDetails ? 'Hide' : 'Show'} Card Meaning
                  </span>
                  {showDetails ? (
                    <ChevronUp className="w-4 h-4"/>
                  ) : (
                    <ChevronDown className="w-4 h-4"/>
                  )}
                </button>
                
                {showDetails && (
                  <div className="mt-4 space-y-4 animate-fade-in text-left">
                    <div>
                      <h4 className="text-[#e89b47] text-sm font-display mb-2">
                        {card.isReversed 
                          ? `What This Means for You${userName ? `, ${userName}` : ''} (Reversed)` 
                          : `What This Means for You${userName ? `, ${userName}` : ''}`}
                      </h4>
                      <p className="text-[#d4c4a8]/70 font-body text-sm leading-relaxed">
                        {card.isReversed ? card.reversed : card.meaning}
                      </p>
                    </div>
                    
                    {card.reflection && (
                      <div>
                        <h4 className="text-[#e89b47]/80 text-sm font-display mb-2">
                          Ask Yourself
                        </h4>
                        <p className="text-[#d4c4a8]/60 font-body text-sm leading-relaxed italic">
                          {card.reflection}
                        </p>
                      </div>
                    )}

                    {card.activation && (
                      <div>
                        <h4 className="text-[#e89b47]/80 text-sm font-display mb-2">
                          Your Next Move
                        </h4>
                        <p className="text-[#d4c4a8]/60 font-body text-sm leading-relaxed">
                          {card.activation}
                        </p>
                      </div>
                    )}

                    {card.isReversed && card.meaning && (
                      <div>
                        <h4 className="text-[#d4c4a8]/60 text-sm font-display mb-2">
                          Upright Meaning
                        </h4>
                        <p className="text-[#d4c4a8]/50 font-body text-sm leading-relaxed">
                          {card.meaning}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col items-center gap-3 mt-6">
            {sessionLocked ? (
              <div
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[#d4c4a8]/20 bg-[#1e2a4a]/40"
                data-testid="pick-another-locked"
              >
                <Lock className="w-3.5 h-3.5 text-[#e89b47]/70 shrink-0" />
                <span className="font-body text-xs text-[#d4c4a8]/60">
                  Three cards per session — refresh the page to draw anew
                </span>
              </div>
            ) : onPickAnother && (
              <>
                <Button
                  onClick={onPickAnother}
                  className="bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] hover:opacity-90 font-display shadow-lg shadow-[#e89b47]/20"
                  data-testid="pick-another-card-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2"/>
                  Pick Another Card
                </Button>
                <span className="font-body text-xs text-[#d4c4a8]/50" data-testid="draws-left-note">
                  {drawsLeft} {drawsLeft === 1 ? 'draw' : 'draws'} left this session · no card repeats
                </span>
              </>
            )}
            <Button
              onClick={onReset}
              variant="outline"
              className="bg-transparent border-[#d4c4a8]/40 text-[#d4c4a8] hover:bg-[#d4c4a8]/10 hover:border-[#d4c4a8] font-display"
              data-testid="back-to-deck-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2"/>
              Back to Deck
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawnCard;
